<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useToaster } from '@/composables/toaster';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton/CButton.vue';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import { previewFileForImagePlaceholder } from '@/utils/elementsUtils';
import { generateWordCloudImage } from '@/api/imageFilters';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { urlToFile } from '@/utils/fileUtils';

const props = defineProps<{
    canvas: CanvasEx;
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
}>();

const model = reactive({
    elementId: 0,
    words: 'customily word cloud example'
});

const disabled = computed(() => !!props.imageElement?.locked);

watch(
    () => props.imageElement,
    (element) => {
        if (!element || !element.wordCloudConfig) return;
        model.elementId = element.id;
    },
    { immediate: true }
);

const updateElementId = () => {
    const element = props.imageElement;
    if (!element) return;
    element.id = model.elementId;
};

const toaster = useToaster();
const generateWordCloud = async () => {
    const element = props.imageElement;
    const config = element?.wordCloudConfig;
    if (!config) return;

    if (!element.testingShapeUrl) {
        toaster.error('Add at least 1 shape to generate a word cloud');
        return;
    }

    try {
        emit('trigger-loading', true, 'Generating word cloud...');
        const file = await urlToFile(element.testingShapeUrl);
        const image = await generateWordCloudImage(file, {
            text: model.words,
            background_color: config.bgColor,
            max_font_size: config.maxFontSize,
            min_font_size: config.minFontSize,
            max_words: config.maxWords,
            use_image_color: config.useImageColors,
            font_url: buildCloudFrontUrl(config.font, false, true)
        });
        await previewFileForImagePlaceholder(props.canvas, element, image, 'base64');
    } catch {
        toaster.error('An error occurred while generating the Word Cloud image');
    } finally {
        emit('trigger-loading', false);
    }
};
</script>

<template>
    <div class="word-cloud-tester-wrapper">
        <c-input
            v-model="model.elementId"
            class="w-25"
            type="number"
            :min="1"
            placeholder="1"
            :pattern="/^[1-9]\d*$/"
            :disabled="disabled"
            label="Element ID"
            @input="updateElementId"
        />
        <c-input v-model="model.words" class="flex-fill" label="Try typing words to use in Word Cloud" />
        <c-button
            class="ml-auto align-self-end"
            variant="light"
            text="Generate Word Cloud"
            :disabled="!model.words.length"
            @click="generateWordCloud"
        />
    </div>
</template>

<style scoped lang="scss">
.word-cloud-tester-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.625rem;
}
</style>
